import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';
import { supabase } from '../../../lib/supabase';

const ConnectionRequestModal = ({ partner, currentUser, onClose }) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  // Predefined message templates
  const messageTemplates = [
    {
      id: 'booking',
      title: 'Demande de booking',
      message: `Bonjour ${partner?.business_name || partner?.user_profiles?.full_name},\n\nJe suis intéressé(e) par vos services pour un événement à venir. Pourriez-vous me contacter pour discuter des détails ?\n\nCordialement,\n${currentUser?.full_name}`
    },
    {
      id: 'collaboration',
      title: 'Proposition de collaboration',
      message: `Bonjour ${partner?.business_name || partner?.user_profiles?.full_name},\n\nJ'aimerais explorer des opportunités de collaboration entre nos activités. Seriez-vous disponible pour en discuter ?\n\nCordialement,\n${currentUser?.full_name}`
    },
    {
      id: 'networking',title: 'Mise en réseau',
      message: `Bonjour ${partner?.business_name || partner?.user_profiles?.full_name},\n\nJe trouve votre profil très intéressant et j'aimerais échanger avec vous sur nos expériences dans l'industrie musicale.\n\nCordialement,\n${currentUser?.full_name}`
    },
    {
      id: 'representation',title: 'Demande de représentation',
      message: `Bonjour ${partner?.business_name || partner?.user_profiles?.full_name},\n\nJe recherche actuellement un(e) agent/représentant(e) pour développer ma carrière. Votre profil m'intéresse beaucoup. Pourriez-vous me recontacter ?\n\nCordialement,\n${currentUser?.full_name}`
    }
  ];

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template?.id);
    setMessage(template?.message);
  };

  // Handle send connection request
  const handleSendRequest = async () => {
    if (!message?.trim()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Create or find existing conversation
      const { data: existingConversation, error: findError } = await supabase?.from('conversations')?.select('*')?.eq('type', 'direct')?.limit(1)?.single();

      let conversationId;

      if (findError || !existingConversation) {
        // Create new conversation
        const { data: newConversation, error: createError } = await supabase?.from('conversations')?.insert({
            name: `Conversation avec ${partner?.business_name || partner?.user_profiles?.full_name}`,
            type: 'direct',
            created_by: currentUser?.id
          })?.select()?.single();

        if (createError) {
          throw createError;
        }

        conversationId = newConversation?.id;

        // Add participants
        await supabase?.from('conversation_participants')?.insert([
            {
              conversation_id: conversationId,
              user_id: currentUser?.id
            },
            {
              conversation_id: conversationId,
              user_id: partner?.user_profiles?.id
            }
          ]);
      } else {
        conversationId = existingConversation?.id;
      }

      // Send the message
      const { error: messageError } = await supabase?.from('messages')?.insert({
          conversation_id: conversationId,
          sender_id: currentUser?.id,
          content: message?.trim(),
          message_type: 'text'
        });

      if (messageError) {
        throw messageError;
      }

      // Create notification for the recipient
      await supabase?.from('notifications')?.insert({
          user_id: partner?.user_profiles?.id,
          title: 'Nouvelle demande de connexion',
          message: `${currentUser?.full_name} souhaite se connecter avec vous`,
          type: 'connection_request',
          related_id: conversationId
        });

      setIsSent(true);
      
      // Auto close after success message
      setTimeout(() => {
        onClose?.();
      }, 2000);

    } catch (error) {
      console.error('Error sending connection request:', error);
      // Handle error (could show error state)
    } finally {
      setIsLoading(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose?.();
    }
  };

  // Success state
  if (isSent) {
    return (
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-card rounded-lg border border-border p-8 shadow-lg max-w-md w-full text-center">
          <div className="mb-4">
            <div className="h-16 w-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={32} className="text-success" />
            </div>
            <h3 className="text-xl font-semibold text-card-foreground mb-2">
              Message envoyé !
            </h3>
            <p className="text-muted-foreground">
              Votre demande de connexion a été envoyée à {partner?.business_name || partner?.user_profiles?.full_name}.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-card rounded-lg border border-border shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-card-foreground">
              Demande de connexion
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Envoyer un message à {partner?.business_name || partner?.user_profiles?.full_name}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="h-8 w-8 p-0"
          />
        </div>

        <div className="p-6">
          {/* Partner Info */}
          <div className="flex items-center space-x-4 mb-6 p-4 bg-muted/30 rounded-lg">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              {partner?.user_profiles?.avatar_url ? (
                <img
                  src={partner?.user_profiles?.avatar_url}
                  alt={partner?.user_profiles?.full_name}
                  className="h-12 w-12 rounded-full object-cover"
                />
              ) : (
                <Icon name="User" size={24} className="text-muted-foreground" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-card-foreground">
                {partner?.business_name || partner?.user_profiles?.full_name}
              </h3>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Briefcase" size={14} />
                <span>{partner?.partner_type}</span>
                {partner?.city && (
                  <>
                    <span>•</span>
                    <Icon name="MapPin" size={14} />
                    <span>{partner?.city}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Message Templates */}
          <div className="mb-6">
            <h4 className="font-medium text-card-foreground mb-3">
              Modèles de messages
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {messageTemplates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => handleTemplateSelect(template)}
                  className={cn(
                    "p-3 text-left rounded-lg border transition-colors",
                    selectedTemplate === template?.id
                      ? "border-accent bg-accent/10" :"border-border hover:bg-accent/5"
                  )}
                >
                  <h5 className="font-medium text-sm text-card-foreground">
                    {template?.title}
                  </h5>
                </button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Votre message *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Écrivez votre message personnalisé..."
              rows={8}
              className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {message?.length || 0}/500 caractères
            </p>
          </div>

          {/* Professional Tips */}
          <div className="mb-6 p-4 bg-accent/10 rounded-lg">
            <h5 className="font-medium text-sm text-card-foreground mb-2 flex items-center space-x-2">
              <Icon name="Lightbulb" size={16} className="text-accent" />
              <span>Conseils pour un message professionnel</span>
            </h5>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Présentez-vous clairement et mentionnez votre expérience</li>
              <li>• Soyez spécifique sur ce que vous recherchez</li>
              <li>• Mentionnez pourquoi ce partenaire vous intéresse</li>
              <li>• Proposez une rencontre ou un appel téléphonique</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={handleSendRequest}
              disabled={!message?.trim() || isLoading}
              loading={isLoading}
              iconName="Send"
              iconPosition="left"
            >
              Envoyer la demande
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionRequestModal;